import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaMusic, FaListUl, FaUsers, FaUserShield, FaSignOutAlt, FaBars, FaTimes, FaUserCircle, FaShareAlt, FaTags } from 'react-icons/fa';
import { ROLE_LABELS } from '../types';
import AvisoDemo from './AvisoDemo';
import InicialGrupo from './InicialGrupo';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Escala de interfaz (accesibilidad), persistida por dispositivo
  const [uiScale, setUiScale] = useState(() => parseFloat(localStorage.getItem('uiScale') || '1') || 1);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--ui-scale', String(uiScale));
    document.documentElement.style.fontSize = `${uiScale * 100}%`;
    localStorage.setItem('uiScale', String(uiScale));
  }, [uiScale]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  if (isLoading) {
    return <div className="p-5 text-center fs-4">Cargando…</div>;
  }
  if (!isAuthenticated) {
    return <RedirectToLogin />;
  }

  const changeScale = (delta: number) =>
    setUiScale((s) => Math.min(1.5, Math.max(0.9, Math.round((s + delta) * 100) / 100)));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/canciones', icon: <FaMusic />, label: 'Canciones' },
    { path: '/listas', icon: <FaListUl />, label: 'Listas' },
    ...(user?.role === 'administrador' || user?.role === 'editor'
      ? [{ path: '/categorias', icon: <FaTags />, label: 'Categorías' }]
      : []),
    { path: '/compartir', icon: <FaShareAlt />, label: 'Compartir' },
    ...(user?.role === 'administrador' ? [{ path: '/usuarios', icon: <FaUsers />, label: 'Usuarios' }] : []),
    ...(user?.isSuperAdmin ? [{ path: '/super-admin', icon: <FaUserShield />, label: 'Grupos' }] : [])
  ];

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <div className="layout">
      {isMobile && (
        <button className="menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Abrir menú">
          <FaBars />
        </button>
      )}

      {isMobile && sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${isMobile ? (sidebarOpen ? 'open' : 'closed') : ''}`}>
        <div className="sidebar-header">
          <img src="/logo.svg" alt="" className="sidebar-logo" />
          <div className="sidebar-titulos">
            <div className="sidebar-brand">Mi Ensamble</div>
            {/* La inicial de color distingue el grupo sin leer: hace falta
                cuando alguien administra dos. */}
            <div className="sidebar-tenant">
              <InicialGrupo nombre={user?.tenantName} tamano={22} />
              <span>{user?.tenantName || 'Mi grupo'}</span>
            </div>
          </div>
          {isMobile && (
            <button className="sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Cerrar menú">
              <FaTimes />
            </button>
          )}
        </div>

        <Link to="/mi-cuenta" className="sidebar-user" title="Mi cuenta">
          <FaUserCircle className="sidebar-avatar" />
          <div>
            <div className="sidebar-user-name">{user?.name}</div>
            <span className="sidebar-role">
              {user?.isSuperAdmin ? 'Super-admin' : ROLE_LABELS[user!.role]}
            </span>
          </div>
        </Link>

        <nav className="sidebar-nav" aria-label="Principal">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="scale-control" role="group" aria-label="Tamaño de la interfaz">
            <span className="scale-label">Tamaño</span>
            <button onClick={() => changeScale(-0.1)} disabled={uiScale <= 0.9} aria-label="Reducir tamaño">A−</button>
            <span className="scale-value">{Math.round(uiScale * 100)}%</span>
            <button onClick={() => changeScale(0.1)} disabled={uiScale >= 1.5} aria-label="Aumentar tamaño">A+</button>
          </div>
          <button className="nav-item logout" onClick={handleLogout}>
            <span className="nav-icon"><FaSignOutAlt /></span>
            Salir
          </button>
        </div>
      </aside>

      <main className="main">
        <AvisoDemo />
        <Outlet />
      </main>
    </div>
  );
};

const RedirectToLogin: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/login', { replace: true });
  }, [navigate]);
  return null;
};

export default Layout;
